<template>
  <v-dialog
    :value="value"
    max-width="1120"
    persistent
    scrollable
    content-class="ds-wizard-dialog relationship-editor-dialog"
    @input="$emit('input', $event)">
    <v-card class="ds-dialog relationship-editor">
      <div class="relationship-editor__shell">
        <aside class="relationship-editor__sidebar" aria-label="Relationship setup steps">
          <div class="relationship-editor__brand">
            <div class="relationship-editor__brand-icon"><v-icon color="white">mdi-vector-link</v-icon></div>
            <div>
              <strong>{{ isEditMode ? 'Edit relationship' : 'New relationship' }}</strong>
              <span :title="registerName">{{ registerName }}</span>
            </div>
          </div>
          <nav class="relationship-editor__steps">
            <div
              v-for="(item, index) in steps"
              :key="item.key"
              class="relationship-editor__step"
              :class="sidebarStepClass(index + 1)"
              :aria-current="step === index + 1 ? 'step' : null">
              <span class="relationship-editor__step-number">
                <v-icon v-if="step > index + 1" x-small color="white">mdi-check</v-icon>
                <template v-else>{{ index + 1 }}</template>
              </span>
              <span class="relationship-editor__step-copy">
                <strong>{{ item.label }}</strong>
                <small>{{ item.description }}</small>
              </span>
              <v-icon v-if="step === index + 1" small color="white">mdi-chevron-right</v-icon>
            </div>
          </nav>
          <div class="relationship-editor__sidebar-status">
            <span>Progress</span>
            <strong>{{ Math.round((step - 1) / (steps.length - 1) * 100) }}%</strong>
            <v-progress-linear
              :value="(step - 1) / (steps.length - 1) * 100"
              height="4"
              color="secondary"
              background-color="rgba(255,255,255,0.2)"
              rounded></v-progress-linear>
          </div>
        </aside>

        <div class="relationship-editor__pane">
      <header class="ds-dialog__header">
        <div class="ds-dialog__header-icon">
          <v-icon color="primary">mdi-vector-link</v-icon>
        </div>
        <div class="ds-dialog__header-text">
          <span ref="dialogTitle" class="ds-dialog__title" tabindex="-1">{{ currentStepHeading }}</span>
          <span class="ds-dialog__subtitle">{{ currentStepSubheading }}</span>
        </div>
        <v-chip small outlined class="relationship-editor__scope-chip" :title="scopeChipTitle">
          <v-icon small>{{ scopeIcon }}</v-icon>
          <span class="relationship-editor__scope-chip-label">{{ scope.label }}</span>
        </v-chip>
        <v-btn icon :disabled="saving" aria-label="Close relationship editor" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <v-card-text class="ds-dialog__body relationship-editor__body">
        <relationship-refusal-banner v-if="problem && problemCopyResolved" class="mb-5" :copy="problemCopyResolved">
          <v-btn v-if="step !== 1 && hasRoutedScopeError" small outlined color="primary" @click="step = 1">
            <v-icon left small>mdi-pencil-outline</v-icon>Edit target settings
          </v-btn>
        </relationship-refusal-banner>
        <div v-else-if="problem" class="ds-info-box ds-info-box--error mb-5" role="alert">
          <v-icon color="error">mdi-alert-circle-outline</v-icon>
          <div>
            <strong>{{ problemTitle }}</strong>
            <div>{{ problem.detail || 'Review the fields below and try again. No relationship change was applied.' }}</div>
          </div>
        </div>

        <section v-if="step === 1" aria-label="Relationship details">
          <v-row>
            <v-col cols="12" md="6">
              <div class="relationship-field-heading">
                <label class="ds-field-label" for="relationship-name">
                  Name <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
                </label>
                <relationship-field-help
                  label="relationship name"
                  text="Identifies this relationship in lists and run history. Renaming it does not change how rows are matched." />
              </div>
              <v-text-field
                id="relationship-name"
                v-model.trim="form.name"
                outlined
                dense
                placeholder="e.g. Scaffold records"
                :error-messages="fieldErrors('name')"
                hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <div class="relationship-field-heading">
                <label class="ds-field-label" for="relationship-source">
                  Register source field <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
                </label>
                <relationship-field-help
                  label="register source field"
                  text="Reads this field from each applicable register row and uses its value to find a target. Choose a stable field whose values identify targets." />
              </div>
              <v-autocomplete
                id="relationship-source"
                v-model="form.sourceFieldKey"
                :items="sourceFieldOptions"
                outlined
                dense
                :disabled="isEditMode"
                :hint="isEditMode ? 'The source field is fixed once a relationship exists. Create a new relationship to read from a different field.' : ''"
                :persistent-hint="isEditMode"
                placeholder="Choose a stable register field"
                :error-messages="fieldErrors('sourceFieldKey')"
                no-data-text="No register field matches your search"
                hide-details="auto">
                <template v-slot:selection="{ item }">
                  <span class="text-truncate">{{ item.text }}</span>
                  <small v-if="showSourceKey(item)" class="relationship-source-key ml-2">{{ item.fieldKey }}</small>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                              <v-list-item-subtitle v-if="showSourceKey(item)">Field key - {{ item.fieldKey }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <relationship-path-rail
            class="my-6"
            auto-expand
            :source="selectedSourceLabel"
            :target="selectedTargetLabel"
            :match-field="selectedMatchFieldLabel"
            :resolution-mode="resolutionModeLabel"
            :projections="railProjections" />

          <v-row>
            <v-col cols="12" md="6">
              <div class="relationship-field-heading">
                <label class="ds-field-label" for="relationship-target">
                  Target type <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
                </label>
                <relationship-field-help
                  label="target type"
                  text="Choose the kind of related record that register rows should find or create." />
              </div>
              <v-autocomplete
                id="relationship-target"
                v-model="form.targetKind"
                :items="targetOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                placeholder="Choose a record type"
                no-data-text="No record types are available for this register"
                :loading="resolvingSchema"
                :error-messages="fieldErrors('targetKind')"
                hide-details="auto"
                @change="onTargetChanged">
                <template v-slot:selection="{ item }">
                  <span class="text-truncate">{{ item.text }}</span>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-icon class="mr-3"><v-icon color="primary">mdi-cube-outline</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                    <v-list-item-subtitle>{{ scope.label }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <div class="relationship-field-heading">
                <label class="ds-field-label" for="relationship-match-field">
                  Match target by <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
                </label>
                <relationship-field-help
                  label="target match field"
                  text="Choose the field used to find an existing record. If more than one record matches, that row is left for review." />
              </div>
              <v-select
                id="relationship-match-field"
                v-model="form.matchField"
                :items="matchFieldOptions"
                outlined
                dense
                :loading="resolvingSchema"
                :disabled="!form.targetKind || resolvingSchema"
                placeholder="Choose the target identity field"
                no-data-text="No matching field is available for this record type"
                :error-messages="fieldErrors('matchField')"
                hide-details="auto" />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <div class="relationship-field-heading">
                <label class="ds-field-label" for="relationship-on-miss">When no target matches</label>
                <relationship-field-help
                  label="no-match behavior"
                  text="Choose whether a row with no match should wait for review or create a new record after you approve the run." />
              </div>
              <v-select
                id="relationship-on-miss"
                v-model="form.onMiss"
                :items="onMissOptions"
                outlined
                dense
                :error-messages="fieldErrors('onMiss')"
                hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <div class="relationship-field-heading">
                <label class="ds-field-label" for="relationship-display-role">Register display role</label>
                <relationship-field-help
                  label="register display role"
                  text="Controls whether linked targets appear as the default 3D highlight or surrounding context. It does not change matching or create data." />
              </div>
              <v-select
                id="relationship-display-role"
                v-model="form.displayRole"
                :items="displayRoleOptions"
                outlined
                dense
                hide-details="auto" />
            </v-col>
          </v-row>

          <p class="relationship-population mt-2">
            <v-icon size="16" color="primary">{{ rowsIncluded.icon }}</v-icon>
            <span>{{ rowsIncluded.note }}</span>
          </p>
        </section>

        <section v-else-if="step === 2" aria-labelledby="relationship-mapping-title">
          <div class="relationship-mapping-toolbar mb-5">
            <div>
              <strong id="relationship-mapping-title">Keep register and {{ selectedTargetLabel || 'related record' }} fields synchronized</strong>
              <span>Optional. Map a register field to the related property that should stay synchronized with it.</span>
            </div>
            <div class="relationship-mapping-actions">
              <span class="relationship-mapping-count">{{ mappings.length }} {{ mappings.length === 1 ? 'mapping' : 'mappings' }}</span>
              <v-btn
                small
                outlined
                color="primary"
                class="relationship-mapping-btn"
                :disabled="!destinationOptions.length"
                @click="addMapping">
                <v-icon left small>mdi-plus</v-icon>Add mapping
              </v-btn>
            </div>
          </div>

          <div class="mapping-safety-summary mb-5" role="note">
            <v-icon color="primary">mdi-swap-horizontal</v-icon>
            <div>
              <strong>Mapped fields stay synchronized automatically</strong>
              <span>A newly created record starts with the register values. An existing record is first linked without overwriting either side; later changes flow automatically in both directions.</span>
            </div>
          </div>

          <div
            v-if="!destinationOptions.length"
            class="ds-empty-state py-8"
            :class="{ 'relationship-schema-unavailable': destinationSchemaIssue }"
            :role="destinationSchemaIssue ? 'alert' : 'status'">
            <v-icon class="ds-empty-state__icon">{{ destinationSchemaIssue ? 'mdi-database-alert-outline' : 'mdi-database-search-outline' }}</v-icon>
            <div class="ds-empty-state__title">{{ destinationSchemaReadOnly ? 'Properties cannot be synchronized here' : destinationSchemaIssue ? 'Properties are temporarily unavailable' : canOfferCustomField ? 'Create the first custom property' : 'No writable properties are available' }}</div>
            <div class="ds-empty-state__description">
              <template v-if="destinationSchemaIssue">
                We could not load the available properties. You can continue without mapped fields, or try again later.
              </template>
              <template v-else-if="destinationSchemaReadOnly">
                You can still create the relationship, but this record type does not expose writable properties for synchronization.
              </template>
              <template v-else-if="canOfferCustomField">
                This target has no existing writable properties. Create a custom property and map it to a register field.
              </template>
              <template v-else>
                This record type has no writable properties available for automatic synchronization.
              </template>
            </div>
            <v-btn v-if="canOfferCustomField" class="mt-4" small outlined color="primary" @click="openCustomField()">
              <v-icon left small>mdi-plus</v-icon>Create custom property
            </v-btn>
          </div>

          <div v-if="destinationSchemaAnalysis.valid" class="property-support mb-5" role="status">
            <div class="property-support__header">
              <div class="property-support__copy">
                <v-icon color="primary">mdi-shape-outline</v-icon>
                <div>
                  <strong>Supported target properties</strong>
                  <span v-if="linkDestinationCount">Choose a built-in property, a link to another record, or an existing custom property.</span>
                  <span v-else>Choose either a built-in property or an existing custom property.</span>
                </div>
              </div>
              <v-btn
                v-if="canOfferCustomField"
                small
                outlined
                color="primary"
                class="property-support__cta"
                @click="openCustomField()">
                <v-icon left small>mdi-plus</v-icon>New custom property
              </v-btn>
            </div>
            <div class="property-support__stats">
              <v-chip v-if="builtInDestinationCount" small outlined class="property-stat-chip property-stat-chip--builtin">
                <v-icon left x-small color="blue">mdi-database</v-icon>{{ builtInDestinationCount }} Built-in
              </v-chip>
              <v-chip v-if="linkDestinationCount" small outlined color="teal" class="property-stat-chip property-stat-chip--link">
                <v-icon left x-small color="teal">mdi-link-variant</v-icon>{{ linkDestinationCount }} Links
              </v-chip>
              <v-chip v-if="customDestinationCount" small outlined color="purple" class="property-stat-chip property-stat-chip--custom">
                <v-icon left x-small color="purple">mdi-puzzle-outline</v-icon>{{ customDestinationCount }} Custom
              </v-chip>
              <span class="property-support__note">Properties for selected target type</span>
            </div>
            <small v-if="dynamicDefinitionGroups.length && !canManageSchema">
              You can use existing custom properties. Creating a new one requires permission to manage the target schema.
            </small>
            <small v-else-if="!dynamicDefinitionGroups.length">
              This target supports existing properties only; it does not allow new custom properties from this workflow.
            </small>
          </div>

          <v-expand-transition>
            <div v-if="customFieldOpen" class="custom-property-editor mb-5" role="region" aria-labelledby="custom-property-title">
              <div class="custom-property-editor__header">
                <div>
                  <strong id="custom-property-title">Create and map a custom property</strong>
                  <span>The property is added to {{ selectedTargetLabel }} and selected in this mapping.</span>
                </div>
                <v-btn icon :disabled="creatingCustomField" aria-label="Close custom property form" @click="closeCustomField">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>

              <div v-if="customFieldProblem" class="ds-info-box ds-info-box--error my-3" role="alert">
                <v-icon color="error">mdi-alert-circle-outline</v-icon>
                <div>
                  <strong>Custom property was not created</strong>
                  <div>{{ customFieldProblem.detail }}</div>
                </div>
              </div>

              <v-row dense class="mt-2">
                <v-col v-if="customFieldStoreOptions.length > 1" cols="12" md="4">
                  <div class="relationship-field-heading">
                    <label class="ds-field-label" for="custom-property-set">Property set</label>
                    <relationship-field-help
                      label="custom property set"
                      text="Choose where this custom property belongs. Most target types have only one property set." />
                  </div>
                  <v-select
                    id="custom-property-set"
                    v-model="customFieldDraft.storeKey"
                    :items="customFieldStoreOptions"
                    outlined
                    dense
                    hide-details="auto"
                    @change="onCustomFieldStoreChanged" />
                </v-col>
                <v-col cols="12" :md="customFieldStoreOptions.length > 1 ? 4 : 7">
                  <div class="relationship-field-heading">
                    <label class="ds-field-label" for="custom-property-name">Property name</label>
                    <relationship-field-help
                      label="custom property name"
                      text="The name shown to operators on the target record. A property with the same name in this scope will be reused only when its type also matches." />
                  </div>
                  <v-text-field
                    id="custom-property-name"
                    ref="customFieldName"
                    v-model.trim="customFieldDraft.name"
                    outlined
                    dense
                    maxlength="200"
                    placeholder="e.g. Merchant reference"
                    hide-details="auto"
                    @keyup.enter="createCustomField" />
                </v-col>
                <v-col cols="12" :md="customFieldStoreOptions.length > 1 ? 4 : 5">
                  <div class="relationship-field-heading">
                    <label class="ds-field-label" for="custom-property-type">Value type</label>
                    <relationship-field-help
                      label="custom property value type"
                      text="Choose the kind of value stored in this property. The available choices come from the selected target type." />
                  </div>
                  <v-select
                    id="custom-property-type"
                    v-model="customFieldDraft.dataType"
                    :items="customFieldTypeOptions"
                    outlined
                    dense
                    hide-details="auto" />
                </v-col>
              </v-row>

              <div class="custom-property-editor__footer">
                <span>This creates the property now; the relationship itself is still saved as inactive.</span>
                <div>
                  <v-btn text :disabled="creatingCustomField" @click="closeCustomField">Cancel</v-btn>
                  <v-btn color="primary" depressed :loading="creatingCustomField" :disabled="!canCreateCustomField" @click="createCustomField">
                    Create and use property
                  </v-btn>
                </div>
              </div>
            </div>
          </v-expand-transition>

          <div v-if="customFieldNotice" class="ds-info-box ds-info-box--info mb-4" role="status">
            <v-icon color="success">mdi-check-circle-outline</v-icon>
            <div>
              <strong>Custom property ready</strong>
              <div>{{ customFieldNotice }}</div>
            </div>
          </div>

          <div v-if="destinationOptions.length && !mappings.length" class="relationship-mapping-empty" role="status">
            <v-icon color="primary">mdi-call-split</v-icon>
            <div>
              <strong>No fields selected</strong>
              <span>This relationship will only connect records. Add a mapping to keep a field synchronized automatically in both directions.</span>
            </div>
            <v-btn small outlined color="primary" @click="addMapping">Add mapping</v-btn>
          </div>

          <div
            v-for="(mapping, index) in mappings"
            :key="mapping.clientId"
            class="mapping-row mb-5">
            <div class="mapping-row__number">{{ index + 1 }}</div>
            <div class="mapping-row__fields">
              <v-row dense>
                <v-col cols="12" md="5">
                  <div class="relationship-field-heading">
                    <label class="ds-field-label" :for="`mapping-source-${mapping.clientId}`">Register field</label>
                    <relationship-field-help
                      label="mapping register field"
                      text="Choose the register field that should stay synchronized with a related record property." />
                  </div>
                  <v-autocomplete
                    :id="`mapping-source-${mapping.clientId}`"
                    v-model="mapping.sourceFieldKey"
                    :items="sourceFieldOptions"
                    outlined
                    dense
                    no-data-text="No register field matches your search"
                    :error-messages="mappingFieldErrors(index, 'sourceFieldKey')"
                    hide-details="auto"
                    @change="onMappingSourceChanged(mapping)">
                    <template v-slot:selection="{ item }">
                      <span class="text-truncate">{{ item.text }}</span>
                      <small v-if="showSourceKey(item)" class="relationship-source-key ml-2">{{ item.fieldKey }}</small>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                              <v-list-item-subtitle v-if="showSourceKey(item)">Field key - {{ item.fieldKey }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="5">
                  <div class="relationship-field-heading">
                    <label class="ds-field-label" :for="`mapping-destination-${mapping.clientId}`">Target property</label>
                    <relationship-field-help
                      label="mapping target destination"
                      text="Choose the built-in or custom property that should stay synchronized with the register field." />
                  </div>
                  <v-autocomplete
                    :id="`mapping-destination-${mapping.clientId}`"
                    v-model="mapping.destination"
                    :items="destinationItemsFor(mapping)"
                    item-text="text"
                    item-value="key"
                    return-object
                    outlined
                    dense
                    :no-data-text="destinationNoDataText"
                    :error-messages="mappingFieldErrors(index, 'destination')"
                    hide-details="auto"
                    :menu-props="{
                      maxHeight: 420,
                      offsetY: true,
                      contentClass: 'destination-picker-menu',
                      nudgeBottom: 4,
                      minWidth: 380,
                      maxWidth: 440,
                    }"
                    @focus="syncDestinationFilter(mapping)"
                    @change="onMappingDestinationChanged(mapping)">
                    <template v-slot:prepend-item>
                      <div class="destination-picker-controls" @mousedown.stop>
                        <div class="destination-picker-segmented" role="group" aria-label="Target property type">
                          <button
                            v-for="namespaceTab in destinationNamespaceTabs"
                            :key="namespaceTab.value"
                            type="button"
                            :aria-pressed="destinationNamespaceFilter === namespaceTab.value"
                            class="destination-picker-segmented__item"
                            :class="{ 'destination-picker-segmented__item--active': destinationNamespaceFilter === namespaceTab.value }"
                            @click.stop="onDestinationNamespaceChanged(namespaceTab.value)">
                            <v-icon x-small class="destination-picker-segmented__icon">{{ namespaceTab.icon }}</v-icon>
                            <span class="destination-picker-segmented__label">{{ namespaceTab.text }}</span>
                            <span class="destination-picker-segmented__count">{{ namespaceTab.count }}</span>
                          </button>
                        </div>
                        <v-select
                          v-if="destinationNamespaceFilter === 'native'"
                          v-model="destinationCategoryFilter"
                          :items="destinationCategoryOptions"
                          class="destination-picker-controls__category"
                          label="Property category"
                          outlined
                          dense
                          hide-details
                          :menu-props="{ offsetY: true, maxHeight: 260 }" />
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <span class="text-truncate">{{ item.label }}</span>
                      <v-chip x-small outlined :color="namespaceColor(item.namespace)" class="ml-2">{{ propertyKindLabel(item) }}</v-chip>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title class="d-flex align-center justify-space-between">
                          <span class="text-truncate mr-2">{{ item.label }}</span>
                          <v-chip x-small outlined :color="namespaceColor(item.namespace)" class="flex-shrink-0">{{ propertyKindLabel(item) }}</v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-truncate">{{ propertyKindDescription(item) }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <v-btn
                    v-if="canOfferCustomField"
                    x-small
                    outlined
                    color="primary"
                    class="mapping-row__inline-custom-btn mt-2"
                    @click="openCustomField(mapping)">
                    <v-icon left x-small>mdi-plus-circle-outline</v-icon>Create a custom property for this value
                  </v-btn>
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center justify-end">
                  <v-btn icon color="error" :aria-label="`Remove mapping ${index + 1}`" @click="removeMapping(index)">
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <div v-if="mapping.sourceFieldKey || mapping.destination" class="mapping-row__sentence mt-2">
                <v-icon small color="primary">mdi-swap-horizontal</v-icon>
                <span>
                  Keep
                  <strong>{{ sourceLabel(mapping.sourceFieldKey) || 'the selected register value' }}</strong>
                  and <strong>{{ mapping.destination ? mapping.destination.label : 'the selected target property' }}</strong> synchronized.
                </span>
              </div>

              <v-expansion-panels flat class="mapping-row__advanced mt-2">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span><v-icon small class="mr-2">mdi-tune-variant</v-icon>Advanced value handling</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="d-flex flex-wrap align-center mapping-row__controls">
                      <div class="mapping-row__policy-field">
                        <div class="relationship-field-heading">
                          <label class="ds-field-label" :for="`mapping-create-${mapping.clientId}`">When creating</label>
                          <relationship-field-help
                            label="mapping create behavior"
                            text="Usually the mapped value is copied. You can instead keep the target default or omit this property." />
                        </div>
                        <v-select
                          :id="`mapping-create-${mapping.clientId}`"
                          v-model="mapping.onCreate"
                          :items="mappingCreateOptions"
                          dense
                          outlined
                          hide-details />
                      </div>
                      <div class="mapping-row__policy-field">
                        <div class="relationship-field-heading">
                          <label class="ds-field-label" :for="`mapping-transform-${mapping.clientId}`">Value format</label>
                          <relationship-field-help
                            label="mapping value format"
                            text="Convert the register value only when the target property requires another format, such as number or date." />
                        </div>
                        <v-select
                          :id="`mapping-transform-${mapping.clientId}`"
                          v-model="mapping.transform"
                          :items="transformOptionsFor(mapping)"
                          dense
                          outlined
                          hide-details
                          :disabled="!mapping.destination" />
                      </div>
                      <div class="mapping-row__policy-field mapping-row__policy-field--error">
                        <div class="relationship-field-heading">
                          <label class="ds-field-label" :for="`mapping-error-${mapping.clientId}`">If conversion fails</label>
                          <relationship-field-help
                            label="mapping conversion failure behavior"
                            text="Stop this row for review, or leave only this property empty." />
                        </div>
                        <v-select
                          :id="`mapping-error-${mapping.clientId}`"
                          v-model="mapping.onError"
                          :items="mappingErrorOptions"
                          dense
                          outlined
                          hide-details />
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <div class="mapping-row__fixed-policy mt-2">
                <span><v-icon x-small color="success">mdi-lock-check-outline</v-icon> Later changes synchronize automatically in both directions</span>
              </div>

              <div v-if="mapping.destination && !transformOptionsFor(mapping).length" class="text-caption error--text mt-2">
                This property cannot accept values from the selected register field. Choose another property.
              </div>
            </div>
          </div>

          <v-btn
            v-if="mappings.length && destinationOptions.length"
            block
            outlined
            color="primary"
            class="relationship-mapping-add-bottom mb-2"
            @click="addMapping">
            <v-icon left small>mdi-plus</v-icon>
            Add another mapping
          </v-btn>
        </section>

        <section v-else aria-labelledby="relationship-review-title">
          <div class="relationship-review-grid">
            <div class="relationship-review-card relationship-review-card--wide">
              <span id="relationship-review-title" class="relationship-review-card__label">Relationship path</span>
              <strong>{{ form.name }}</strong>
              <relationship-path-rail
                class="mt-3"
                dense
                :source="selectedSourceLabel"
                :target="selectedTargetLabel"
                :match-field="selectedMatchFieldLabel"
                :resolution-mode="resolutionModeLabel"
                :projections="railProjections" />
            </div>
            <div class="relationship-review-card relationship-review-card--wide relationship-review-summary">
              <div class="relationship-review-summary__row">
                <span class="relationship-review-card__label">Where it applies</span>
                <strong>{{ scope.label }}</strong>
              </div>
              <div class="relationship-review-summary__row">
                <span class="relationship-review-card__label">Related record</span>
                <strong>{{ selectedTargetLabel }}</strong>
              </div>
              <div class="relationship-review-summary__row relationship-review-summary__row--wide">
                <span class="relationship-review-card__label">How records match</span>
                <strong>{{ selectedSourceLabel }} to {{ selectedMatchFieldLabel }}</strong>
                <small>{{ resolutionModeLabel }}. This field is used to find the related record; its value is not copied.</small>
              </div>
              <div class="relationship-review-summary__row">
                <span class="relationship-review-card__label">When no record matches</span>
                <strong>{{ form.onMiss === 'create' ? 'Create a new record' : 'Leave it for review' }}</strong>
              </div>
              <div class="relationship-review-summary__row">
                <span class="relationship-review-card__label">Rows included</span>
                <strong>{{ applicabilityLabel }}</strong>
              </div>
              <div class="relationship-review-summary__row relationship-review-summary__row--wide">
                <span class="relationship-review-card__label">Synchronized fields</span>
                <div v-if="compiledMappings.length" class="mt-1">
                  <div v-for="entry in compiledMappings" :key="entry.clientId" class="relationship-review-map">
                    <span>{{ sourceLabel(entry.sourceFieldKey) }}</span>
                    <span class="relationship-review-map__separator">with</span>
                    <span>{{ entry.destinationLabel }}</span>
                    <v-chip x-small outlined :color="namespaceColor(entry.namespace)">{{ propertyKindLabel(entry) }}</v-chip>
                    <small>{{ mappingPolicyLabel(entry) }}</small>
                  </div>
                </div>
                <span v-else>No fields will be synchronized.</span>
              </div>
            </div>
          </div>

          <div class="ds-mode-banner relationship-save-note mt-5">
            <v-icon class="ds-mode-banner__icon">mdi-lock-outline</v-icon>
            <div class="ds-mode-banner__content">
              <span class="ds-mode-banner__label">Before you save:</span>
              <span class="ds-mode-banner__value">Saving does not change any records. The relationship stays switched off until you enable it.</span>
            </div>
          </div>
        </section>
      </v-card-text>

      <footer class="ds-dialog__footer">
        <v-btn
          outlined
          :disabled="saving"
          class="ds-dialog__btn-back"
          @click="step === 1 ? close() : previousStep()">
          <v-icon v-if="step > 1" left small>mdi-arrow-left</v-icon>{{ step === 1 ? 'Cancel' : 'Back' }}
        </v-btn>
        <v-spacer></v-spacer>
        <span class="ds-dialog__hint mr-3">Step {{ step }} of {{ steps.length }}</span>
        <v-btn
          v-if="step < steps.length"
          color="primary"
          depressed
          :disabled="!canContinue"
          @click="advanceStep">
          Continue
        </v-btn>
        <v-btn
          v-else
          color="primary"
          depressed
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit">
          <v-icon left>mdi-content-save-outline</v-icon>{{ isEditMode ? 'Save changes' : 'Save relationship' }}
        </v-btn>
      </footer>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import RelationshipFieldHelp from './RelationshipFieldHelp.vue';
import RelationshipPathRail from './RelationshipPathRail.vue';
import RelationshipRefusalBanner from './RelationshipRefusalBanner.vue';
import {
  editorScopeFieldErrors,
  isRoutedScopeError,
  problemCopy,
} from '../../config/relationshipRefusalCopy';
import relationshipService, {
  createRelationshipRequestKey,
  toRelationshipProblem,
  RELATIONSHIP_DEFAULT_MATCH_MODE,
  RELATIONSHIP_PATCH_FIELDS,
} from '../../services/registerRelationshipService';
import {
  capabilityCanCreate,
  capabilityCanRenderPoi,
  capabilityLabel,
  capabilityOperationSummary,
  capabilitySupportsScope,
  analyzeDestinationSchema,
  registerOwnershipScope,
  DESTINATION_NAMESPACE_LABELS,
} from '../../config/relationshipUi';
import { FIELD_TYPES } from '@/enums/columnMappings';

let mappingSequence = 0;

export default {
  name: 'RelationshipEditorDialog',
  components: { RelationshipFieldHelp, RelationshipPathRail, RelationshipRefusalBanner },
  props: {
    value: { type: Boolean, default: false },
    register: { type: Object, required: true },
    relationship: { type: Object, default: null },
    capabilities: { type: Array, default: () => [] },
    canManageSchema: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    problem: { type: Object, default: null },
  },
  data() {
    return {
      step: 1,
      resolvingSchema: false,
      steps: [
        {
          key: 'definition',
          label: 'Connect',
          description: 'Register field and related record',
          heading: 'Create a relationship',
          subheading: 'Choose the register field and related record used for matching',
        },
        {
          key: 'mapping',
          label: 'Sync fields',
          description: 'Optional two-way field sync',
          heading: 'Keep fields synchronized',
          subheading: 'Optional: map register fields to related record properties',
        },
        {
          key: 'review',
          label: 'Review',
          description: 'Check and save',
          heading: 'Review relationship',
          subheading: 'Check the setup before saving it as inactive',
        },
      ],
      form: this.emptyForm(),
      mappings: [],
      destinationNamespaceFilter: 'native',
      destinationCategoryFilter: 'all',
      persistedApplicabilityFilter: null,
      customFieldOpen: false,
      creatingCustomField: false,
      customFieldRequestGeneration: 0,
      destinationSchemaRequestId: null,
      customFieldProblem: null,
      customFieldNotice: '',
      customFieldDraft: {
        mappingId: null,
        storeKey: null,
        name: '',
        dataType: null,
      },
      onMissBaseOptions: [
        { text: 'Leave unresolved', value: 'leaveUnresolved' },
      ],
      mappingErrorOptions: [
        { text: 'Block this row', value: 'blockRow' },
        { text: 'Skip this field', value: 'skipField' },
      ],
      mappingCreateOptions: [
        { text: 'Set the mapped value', value: 'set' },
        { text: 'Use the target default', value: 'default' },
        { text: 'Skip this destination', value: 'skip' },
      ],
    };
  },
  computed: {
    ...mapState('REGISTERS/relationships', [
      'registerSchema',
      'destinationSchema',
      'schemaRequestId',
      'workspaceRequestId',
    ]),
    scope() { return registerOwnershipScope(this.register); },
    registerName() { return this.register?.name || 'Register connection'; },
    // Mirrors the workspace rail's scope icons. Kept local rather than shared because this task
    // owns only this file; worth folding into config/relationshipUi.js on the next pass here.
    scopeIcon() {
      if (this.scope.kind === 'facilityGroup') return 'mdi-domain';
      if (this.scope.kind === 'facility') return 'mdi-map-marker-outline';
      return 'mdi-map-marker-question-outline';
    },
    scopeChipTitle() {
      const kind = {
        facility: 'Facility',
        facilityGroup: 'Facility Group',
      }[this.scope.kind] || 'Register owner';
      return `${kind}: ${this.scope.label}`;
    },
    // Row scoping is chosen when a run is previewed, not when the relationship is declared, so this
    // is a statement of fact rather than a control. The only variable is whether a filter was
    // persisted by something other than this editor.
    rowsIncluded() {
      return this.hasPersistedApplicabilityFilter
        ? {
          icon: 'mdi-filter-check-outline',
          note: 'Keeps the row filter this relationship was created with.',
        }
        : {
          icon: 'mdi-table-multiple',
          note: 'Applies to all eligible rows - narrow to selected or filtered rows when you run a preview.',
        };
    },
    isEditMode() { return Boolean(this.relationship?.id); },
    currentStepHeading() { return this.steps[this.step - 1]?.heading || 'Relationship setup'; },
    currentStepSubheading() { return this.steps[this.step - 1]?.subheading || ''; },
    sourceFieldOptions() {
      const columns = this.registerSchema?.columns || this.registerSchema?.Columns || [];
      return columns
        .filter((column) => !column.isSystemColumn && !column.IsSystemColumn)
        .map((column) => ({
          text: column.displayName || column.DisplayName || column.name || column.Name,
          value: column.name || column.Name,
          fieldKey: column.name || column.Name,
          type: this.normalizeMappingValueType(column.dataType || column.DataType),
        }));
    },
    targetOptions() {
      return this.capabilities
        .filter((capability) => capabilitySupportsScope(capability, this.scope.kind))
        .map((capability) => ({
          text: capabilityLabel(capability),
          value: capability.targetKind,
          operationLabel: capabilityCanCreate(capability, this.scope.kind)
            ? 'Link or create'
            : 'Link existing',
          capability,
        }));
    },
    selectedCapability() {
      return this.capabilities.find((capability) => capability.targetKind === this.form.targetKind) || null;
    },
    selectedSourceLabel() { return this.sourceLabel(this.form.sourceFieldKey); },
    selectedTargetLabel() { return this.selectedCapability ? capabilityLabel(this.selectedCapability) : ''; },
    selectedCapabilityOperations() {
      return capabilityOperationSummary(this.selectedCapability, this.scope.kind);
    },
    selectedMatchFieldLabel() {
      return this.matchFieldOptions.find((field) => field.value === this.form.matchField)?.text || this.form.matchField || '';
    },
    resolutionModeLabel() {
      return this.form.onMiss === 'create'
        ? 'Resolve existing or create after approval'
        : 'Resolve existing only';
    },
    displayRoleOptions() {
      const options = [{ text: 'No display role', value: 'none' }];
      if (capabilityCanRenderPoi(this.selectedCapability, this.scope.kind)) {
        options.push(
          { text: 'Default highlight', value: 'highlight' },
          { text: 'Default context', value: 'context' },
        );
      }
      return options;
    },
    matchFieldOptions() {
      const fields = this.selectedCapability?.matchFields
        || this.destinationSchema?.matchFields
        || [];
      return fields.map((field) => ({
        text: field.displayName || field.label || field.fieldKey || field,
        value: field.fieldKey || field.value || field,
      }));
    },
    onMissOptions() {
      const options = [...this.onMissBaseOptions];
      if (capabilityCanCreate(this.selectedCapability, this.scope.kind)) {
        options.push({ text: 'Create a target after human confirmation', value: 'create' });
      }
      return options;
    },
    destinationSchemaAnalysis() {
      return analyzeDestinationSchema(this.destinationSchema, {
        targetKind: this.form.targetKind,
        scopeKind: this.scope.kind,
        scopeId: this.scope.id,
      });
    },
    destinationOptions() { return this.destinationSchemaAnalysis.options; },
    destinationNamespaceTabs() {
      return [
        { text: 'Built-in', value: 'native', count: this.builtInDestinationCount, icon: 'mdi-database-outline' },
        { text: 'Custom', value: 'custom', count: this.customDestinationCount, icon: 'mdi-puzzle-outline' },
        { text: 'Links to other Tags', value: 'link', count: this.linkDestinationCount, icon: 'mdi-link-variant' },
      ];
    },
    destinationCategoryOptions() {
      const categories = new Set(
        this.destinationOptions
          .filter((option) => this.isBuiltInDestination(option))
          .map((option) => option.category || 'Other'),
      );
      return [
        { text: 'All categories', value: 'all' },
        ...[...categories]
          .sort((left, right) => left.localeCompare(right))
          .map((category) => ({ text: category, value: category })),
      ];
    },
    filteredDestinationItems() {
      const namespace = this.destinationNamespaceFilter;
      return this.destinationOptions.filter((option) => {
        if (this.destinationNamespaceFor(option) !== namespace) return false;
        return namespace !== 'native'
          || this.destinationCategoryFilter === 'all'
          || (option.category || 'Other') === this.destinationCategoryFilter;
      });
    },
    destinationNoDataText() {
      const labels = { native: 'built-in', custom: 'custom', link: 'link' };
      const namespace = this.destinationNamespaceFilter;
      // A target type with no Tag-to-Tag link properties (e.g. Pipeline) is easy to misread as "this
      // row can't be linked": row-to-Tag links come from the register field chosen in step 1.
      if (namespace === 'link' && this.linkDestinationCount === 0) {
        const target = this.selectedTargetLabel || 'this target';
        return `${target} has no properties that point to another Tag. To link register rows to ${target} Tags, choose the register field in step 1 (Connect).`;
      }
      return `No ${labels[namespace] || 'writable'} property matches these filters`;
    },
    dynamicDefinitionGroups() { return this.destinationSchemaAnalysis.dynamicDefinitionGroups; },
    builtInDestinationCount() {
      return this.destinationOptions.filter((option) => this.isBuiltInDestination(option)).length;
    },
    linkDestinationCount() {
      return this.destinationOptions.filter((option) => this.isLinkDestination(option)).length;
    },
    customDestinationCount() {
      return this.destinationOptions.length
        - this.builtInDestinationCount
        - this.linkDestinationCount;
    },
    canOfferCustomField() {
      return this.dynamicDefinitionGroups.length > 0
        && this.canManageSchema;
    },
    customFieldStoreOptions() {
      return this.dynamicDefinitionGroups.map((group, index) => ({
        text: this.dynamicDefinitionGroups.length === 1
          ? 'Custom properties'
          : `Custom property set ${index + 1}`,
        value: group.storeKey,
      }));
    },
    selectedCustomFieldGroup() {
      return this.dynamicDefinitionGroups.find(
        (group) => group.storeKey === this.customFieldDraft.storeKey,
      ) || null;
    },
    customFieldTypeOptions() {
      const valueTypes = this.selectedCustomFieldGroup?.valueTypes || [];
      const compatibleTypes = this.customFieldSourceType
        ? valueTypes.filter((value) => this.mappingValueTypesCompatible(
          this.customFieldSourceType,
          value,
        ))
        : valueTypes;
      return compatibleTypes.map((value) => ({
        text: this.dataTypeLabel(value),
        value,
      }));
    },
    customFieldSourceType() {
      const mapping = this.mappings.find(
        (item) => item.clientId === this.customFieldDraft.mappingId,
      );
      return this.sourceValueType(mapping?.sourceFieldKey || this.form.sourceFieldKey);
    },
    canCreateCustomField() {
      return this.canOfferCustomField
        && !this.creatingCustomField
        && Boolean(this.selectedCustomFieldGroup)
        && Boolean(String(this.customFieldDraft.name || '').trim())
        && this.customFieldTypeOptions.some(
          (option) => option.value === this.customFieldDraft.dataType,
        );
    },
    destinationSchemaIssue() { return this.destinationSchemaAnalysis.issue; },
    destinationSchemaReadOnly() {
      return this.destinationSchemaAnalysis.valid && !this.destinationSchemaAnalysis.isWritable;
    },
    hasPersistedApplicabilityFilter() {
      return this.persistedApplicabilityFilter !== null;
    },
    applicabilityLabel() {
      return this.form.applicabilityKind === 'declarativeFilter'
        ? 'Saved filter'
        : 'All register rows';
    },
    refusalCopyContext() {
      const label = typeof this.scope.label === 'string' ? this.scope.label.trim() : '';
      return {
        groupName: this.scope.kind === 'facilityGroup' && label !== 'Facility Group' ? label : '',
        registerKind: this.scope.kind,
        tagTypeLabel: this.selectedTargetLabel,
      };
    },
    problemCopyResolved() { return this.problem ? problemCopy(this.problem, this.refusalCopyContext) : null; },
    hasRoutedScopeError() { return (this.problem?.errors || []).some(isRoutedScopeError); },
    problemTitle() {
      return this.form.targetKind && !this.destinationSchema
        ? 'Record fields are unavailable'
        : 'Relationship could not be saved';
    },
    compiledMappings() {
      return this.mappings
        .filter((mapping) => mapping.sourceFieldKey && mapping.destination?.destination)
        .map((mapping) => ({
          ...mapping,
          destinationLabel: mapping.destination.label,
          namespace: mapping.destination.namespace,
        }));
    },
    railProjections() {
      return this.compiledMappings.map((mapping) => ({
        key: mapping.clientId,
        source: this.sourceLabel(mapping.sourceFieldKey),
        destination: mapping.destinationLabel,
        namespace: this.propertyKindLabel(mapping),
      }));
    },
    canContinue() {
      if (this.step === 1) {
        return Boolean(
          this.form.name
          && this.form.sourceFieldKey
          && this.form.targetKind
          && this.form.matchField
          && this.scope.kind,
        );
      }
      if (this.step === 2) {
        return this.mappings.every((mapping) => this.mappingIsComplete(mapping));
      }
      return true;
    },
    canSubmit() {
      if (!this.canContinue || this.saving) return false;
      return !this.isEditMode || Object.keys(this.buildPayload()).length > 0;
    },
  },
  watch: {
    value: {
      immediate: true,
      async handler(isOpen) {
        if (isOpen) await this.reset();
        else this.invalidateEditorRequests();
      },
    },
  },
  beforeDestroy() {
    this.invalidateEditorRequests();
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        sourceFieldKey: null,
        targetKind: null,
        matchField: null,
        onMiss: 'leaveUnresolved',
        displayRole: 'none',
        applicabilityKind: 'allRows',
      };
    },
    async reset() {
      this.invalidateEditorRequests();
      this.step = 1;
      this.form = this.emptyForm();
      this.mappings = [];
      this.destinationNamespaceFilter = 'native';
      this.destinationCategoryFilter = 'all';
      this.persistedApplicabilityFilter = null;
      this.customFieldOpen = false;
      this.creatingCustomField = false;
      this.customFieldProblem = null;
      this.customFieldNotice = '';
      this.customFieldDraft = {
        mappingId: null,
        storeKey: null,
        name: '',
        dataType: null,
      };
      this.$store.commit('REGISTERS/relationships/SET_DESTINATION_SCHEMA', null);
      this.$store.commit('REGISTERS/relationships/SET_PROBLEM', null);
      if (this.relationship) await this.hydrateRelationship();
      this.$nextTick(() => this.$refs.dialogTitle?.focus());
    },
    async hydrateRelationship() {
      const relationship = this.relationship;
      const applicability = relationship.applicability || { kind: 'allRows' };
      this.persistedApplicabilityFilter = applicability.kind === 'declarativeFilter'
        && applicability.filter && typeof applicability.filter === 'object'
        ? applicability.filter
        : null;
      this.form = {
        name: relationship.name || '',
        sourceFieldKey: relationship.sourceFieldKey || null,
        targetKind: relationship.targetKind || null,
        matchField: relationship.matchField || null,
        onMiss: relationship.onMiss || 'leaveUnresolved',
        displayRole: relationship.displayRole || 'none',
        applicabilityKind: this.persistedApplicabilityFilter ? 'declarativeFilter' : 'allRows',
      };

      if (this.form.targetKind) {
        this.resolvingSchema = true;
        try {
          await this.resolveDestinationSchema({
            targetKind: this.form.targetKind,
            targetContext: relationship.targetContext || null,
          });
        } catch (err) {
          // The typed error is displayed from Vuex; keep the stored declaration visible.
        } finally {
          this.resolvingSchema = false;
        }
      }

      const entries = Array.isArray(relationship.mapping) ? relationship.mapping : [];
      this.mappings = entries.map((entry) => {
        mappingSequence += 1;
        return {
          clientId: `mapping-${mappingSequence}`,
          sourceFieldKey: entry.sourceFieldKey,
          destination: this.findDestination(entry.destination),
          onCreate: entry.onCreate || 'set',
          onExisting: 'ignore',
          direction: 'both',
          transform: this.normalizeTransform(entry.transform),
          onError: entry.onError || 'blockRow',
        };
      });
      this.mappings.forEach((mapping) => {
        const requiredTransform = this.mappingTransformForTypes(
          this.sourceValueType(mapping.sourceFieldKey),
          mapping.destination?.type,
        );
        if (requiredTransform && requiredTransform !== 'identity') {
          this.selectRequiredTransform(mapping);
        }
      });
      const firstDestination = this.mappings.find((mapping) => mapping.destination)?.destination;
      if (firstDestination) this.syncDestinationFilter({ destination: firstDestination });
    },
    findDestination(destination) {
      if (!destination) return null;
      const matches = this.destinationOptions.filter((option) => {
        const candidate = option.destination;
        if (candidate.kind !== destination.kind) return false;
        // Native and link identities are a field key alone. Without this arm a link would fall to
        // the store comparisons below, where two undefined store keys read as a match.
        if (candidate.kind === 'native' || candidate.kind === 'link') {
          return candidate.fieldKey === destination.fieldKey;
        }
        if (candidate.storeKey !== destination.storeKey) return false;
        if (candidate.kind === 'dynamicAttribute') return candidate.definitionRef === destination.definitionRef;
        return candidate.definitionId === destination.definitionId;
      });
      return matches.length === 1 ? matches[0] : null;
    },
    close() {
      if (this.saving) return;
      this.invalidateEditorRequests();
      this.$emit('input', false);
    },
    sidebarStepClass(number) {
      if (number < this.step) return 'relationship-editor__step--complete';
      if (number === this.step) return 'relationship-editor__step--active';
      return 'relationship-editor__step--pending';
    },
    fieldErrors(field) {
      // `/targetScopeKind` is not a form field: those refusals are routed by code to the control
      // the user has to change (Target type or When no target matches), with friendly copy.
      const matched = (this.problem?.errors || [])
        .filter((entry) => !isRoutedScopeError(entry))
        .filter((entry) => String(entry.path || '').toLowerCase().includes(field.toLowerCase()))
        .map((entry) => entry.message || entry.code);
      return [...matched, ...editorScopeFieldErrors(this.problem, field, this.refusalCopyContext)];
    },
    mappingFieldErrors(index, field) {
      const paths = [
        `/mapping/${index}/${field}`,
      ].map((path) => path.toLowerCase());
      return (this.problem?.errors || [])
        .filter((entry) => paths.some((path) => String(entry.path || '').toLowerCase().includes(path)))
        .map((entry) => entry.message || entry.code);
    },
    sourceLabel(key) {
      return this.sourceFieldOptions.find((item) => item.value === key)?.text || key || '';
    },
    sourceLiteral(key) {
      const option = this.sourceFieldOptions.find((item) => item.value === key);
      return String(option?.text || option?.fieldKey || key || '').trim();
    },
    showSourceKey(item) {
      return Boolean(item?.fieldKey && item.fieldKey !== item.text);
    },
    transformOptionsFor(mapping) {
      const requiredTransform = this.mappingTransformForTypes(
        this.sourceValueType(mapping?.sourceFieldKey),
        mapping?.destination?.type,
      );
      if (!requiredTransform) return [];
      const descriptors = mapping?.destination?.allowedTransforms || [];
      const advertised = descriptors.map((descriptor) => {
        if (typeof descriptor === 'string') {
          return {
            text: this.transformLabel(descriptor),
            value: { kind: descriptor, arguments: null },
          };
        }
        const value = descriptor?.value
          || descriptor?.transform
          || (descriptor?.kind && descriptor?.requiresConfiguration !== true
            ? { kind: descriptor.kind, ...(descriptor.defaultParameters || {}) }
            : null);
        if (!value?.kind) return null;
        return {
          text: descriptor.displayName
            || descriptor.label
            || this.transformLabel(descriptor.kind),
          value: this.normalizeTransform(value),
        };
      }).filter(Boolean);
      if (requiredTransform === 'numberToText') {
        return advertised.filter((option) => option.value.kind === requiredTransform);
      }
      return [
        { text: 'No conversion', value: { kind: 'identity', arguments: null } },
        ...advertised.filter(
          (option) => !['identity', 'numberToText'].includes(option.value.kind),
        ),
      ];
    },
    transformLabel(kind) {
      if (kind === 'numberToText') return 'Number ↔ Text';
      if (kind === 'trim') return 'Trim surrounding spaces';
      return kind;
    },
    normalizeTransform(transform) {
      if (!transform?.kind) return { kind: 'identity', arguments: null };
      const kind = transform.kind === 'none' ? 'trim' : transform.kind;
      if (Object.prototype.hasOwnProperty.call(transform, 'arguments')) {
        return { kind, arguments: transform.arguments };
      }
      const argumentsValue = { ...transform };
      delete argumentsValue.kind;
      return {
        kind,
        arguments: Object.keys(argumentsValue).length ? argumentsValue : null,
      };
    },
    mappingIsComplete(mapping) {
      if (!mapping?.sourceFieldKey || !mapping?.destination?.destination) return false;
      if (!this.mappingValueTypesCompatible(
        this.sourceValueType(mapping.sourceFieldKey),
        mapping.destination.type,
      )) return false;
      if (!['set', 'default', 'skip'].includes(mapping.onCreate)) return false;
      if (!['blockRow', 'skipField'].includes(mapping.onError)) return false;
      const transformKind = mapping.transform?.kind;
      return Boolean(transformKind && this.transformOptionsFor(mapping).some((option) => option.value.kind === transformKind));
    },
    mappingPolicyLabel(mapping) {
      const createPolicy = {
        set: 'Copy the register value',
        default: 'Keep the target default',
        skip: 'Leave this property empty',
      }[mapping.onCreate] || 'Review this mapping';
      const conversion = mapping.transform?.kind && mapping.transform.kind !== 'identity'
        ? `, converting it as ${mapping.transform.kind}`
        : '';
      return `${createPolicy}${conversion}. Later changes synchronize automatically in both directions.`;
    },
    async onTargetChanged() {
      this.invalidateEditorRequests();
      this.form.matchField = null;
      this.form.onMiss = 'leaveUnresolved';
      this.form.displayRole = 'none';
      this.mappings = [];
      this.destinationNamespaceFilter = 'native';
      this.destinationCategoryFilter = 'all';
      this.customFieldOpen = false;
      this.customFieldProblem = null;
      this.customFieldNotice = '';
      this.$store.commit('REGISTERS/relationships/SET_DESTINATION_SCHEMA', null);
      if (!this.form.targetKind) return;
      this.resolvingSchema = true;
      try {
        await this.resolveDestinationSchema({
          targetKind: this.form.targetKind,
        });
        if (this.matchFieldOptions.length === 1) {
          this.form.matchField = this.matchFieldOptions[0].value;
        }
      } catch (err) {
        // The Vuex module already holds the typed ProblemDetails for display.
      } finally {
        this.resolvingSchema = false;
      }
    },
    addMapping() {
      mappingSequence += 1;
      const mapping = {
        clientId: `mapping-${mappingSequence}`,
        sourceFieldKey: this.defaultMappingSourceField(),
        destination: null,
        onCreate: 'set',
        onExisting: 'ignore',
        direction: 'both',
        transform: { kind: 'identity', arguments: null },
        onError: 'blockRow',
      };
      this.mappings.push(mapping);
      return mapping;
    },
    // A new mapping starts on the first register field not yet synced and not the match field:
    // the match field only finds the Tag, so syncing it is rarely what the user wants (AVA-602 U11).
    // Falls back to the match field when it is the only field left.
    defaultMappingSourceField() {
      const used = new Set(this.mappings.map((item) => item.sourceFieldKey));
      const match = this.form.sourceFieldKey;
      const next = this.sourceFieldOptions.find((option) => option.value !== match && !used.has(option.value));
      return next ? next.value : (match || null);
    },
    removeMapping(index) { this.mappings.splice(index, 1); },
    destinationNamespaceFor(destinationOption) {
      if (this.isBuiltInDestination(destinationOption)) return 'native';
      if (this.isLinkDestination(destinationOption)) return 'link';
      return 'custom';
    },
    syncDestinationFilter(mapping) {
      if (!mapping?.destination) return;
      this.destinationNamespaceFilter = this.destinationNamespaceFor(mapping.destination);
      if (this.destinationNamespaceFilter !== 'native') this.destinationCategoryFilter = 'all';
    },
    onDestinationNamespaceChanged(namespaceValue) {
      this.destinationNamespaceFilter = String(namespaceValue || 'native').replace(/^#/, '');
      if (this.destinationNamespaceFilter !== 'native') this.destinationCategoryFilter = 'all';
    },
    onMappingSourceChanged(mapping) {
      if (mapping.destination && !this.mappingValueTypesCompatible(
        this.sourceValueType(mapping.sourceFieldKey),
        mapping.destination.type,
      )) {
        mapping.destination = null;
        mapping.transform = { kind: 'identity', arguments: null };
      } else if (mapping.destination) {
        this.selectRequiredTransform(mapping);
      }
      if (this.customFieldOpen
        && this.customFieldDraft.mappingId === mapping.clientId
        && !String(this.customFieldDraft.name || '').trim()) {
        this.customFieldDraft.name = this.sourceLiteral(mapping.sourceFieldKey);
      }
    },
    normalizeMappingValueType(value) {
      const legacyType = {
        [FIELD_TYPES.TEXT]: 'string',
        [FIELD_TYPES.NUMBER]: 'number',
        [FIELD_TYPES.DATE]: 'date',
        [FIELD_TYPES.BOOLEAN]: 'boolean',
      }[value];
      if (legacyType) return legacyType;

      const normalized = String(value || '').trim().toLowerCase();
      if (normalized === 'text' || normalized === 'string') return 'string';
      if (normalized === 'number') return 'number';
      if (normalized === 'boolean') return 'boolean';
      if (normalized === 'date') return 'date';
      return null;
    },
    sourceValueType(sourceFieldKey) {
      return this.sourceFieldOptions.find(
        (option) => option.value === sourceFieldKey,
      )?.type || null;
    },
    mappingTransformForTypes(sourceType, destinationType) {
      const source = this.normalizeMappingValueType(sourceType);
      const destination = this.normalizeMappingValueType(destinationType);
      if (!source || !destination) return null;
      if (source === destination) return 'identity';
      if (source === 'number' && destination === 'string') return 'numberToText';
      return null;
    },
    mappingValueTypesCompatible(sourceType, destinationType) {
      return Boolean(this.mappingTransformForTypes(sourceType, destinationType));
    },
    selectRequiredTransform(mapping) {
      const requiredTransform = this.mappingTransformForTypes(
        this.sourceValueType(mapping?.sourceFieldKey),
        mapping?.destination?.type,
      );
      if (!requiredTransform) return;
      const available = this.transformOptionsFor(mapping);
      if (!available.some((option) => option.value.kind === requiredTransform)) return;
      mapping.transform = { kind: requiredTransform, arguments: null };
    },
    onMappingDestinationChanged(mapping) {
      this.syncDestinationFilter(mapping);
      this.selectRequiredTransform(mapping);
    },
    destinationItemsFor(mapping) {
      const sourceType = this.sourceValueType(mapping?.sourceFieldKey);
      if (!sourceType) return mapping?.destination ? [mapping.destination] : [];
      const filtered = this.filteredDestinationItems.filter(
        (item) => this.mappingValueTypesCompatible(sourceType, item.type)
          && this.transformOptionsFor({
            sourceFieldKey: mapping.sourceFieldKey,
            destination: item,
          }).length > 0,
      );
      const selected = mapping?.destination;
      if (!selected || filtered.some((item) => item.key === selected.key)) return filtered;

      // Vuetify resolves a return-object selection from `items`. The type/category filters are
      // shared by the pickers, so keep this row's already-selected value in its own item source even
      // when another row changes the active filter.
      return [selected, ...filtered];
    },
    // Both predicates accept the schema's display namespace and the short chip label, because the
    // path rail re-feeds its own already-shortened label back through these helpers.
    isBuiltInDestination(item) {
      return item?.namespace === DESTINATION_NAMESPACE_LABELS.native || item?.namespace === 'Built-in';
    },
    isLinkDestination(item) {
      return item?.namespace === DESTINATION_NAMESPACE_LABELS.link || item?.namespace === 'Link';
    },
    propertyKindLabel(item) {
      if (this.isBuiltInDestination(item)) return 'Built-in';
      if (this.isLinkDestination(item)) return 'Link';
      return 'Custom';
    },
    propertyKindDescription(item) {
      // A link's declared type describes the name the cell holds, not the value that gets written,
      // so saying "Text" here would misdescribe what the mapping does.
      if (this.isLinkDestination(item)) return 'Link property · Names a related record';
      const type = item?.type ? this.dataTypeLabel(item.type) : 'Value';
      const category = this.isBuiltInDestination(item) ? ` · ${item.category || 'Other'}` : '';
      return `${this.propertyKindLabel(item)} property · ${type}${category}`;
    },
    dataTypeLabel(value) {
      const labels = {
        string: 'Text',
        text: 'Text',
        number: 'Number',
        integer: 'Whole number',
        decimal: 'Decimal number',
        boolean: 'Yes / No',
        date: 'Date',
        dateTime: 'Date and time',
        datetime: 'Date and time',
      };
      return labels[value] || String(value || 'Value')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (character) => character.toUpperCase());
    },
    openCustomField(mapping = null) {
      if (!this.canOfferCustomField) return;
      this.invalidateCustomFieldRequest();
      const group = this.dynamicDefinitionGroups[0];
      this.customFieldProblem = null;
      this.customFieldNotice = '';
      this.customFieldDraft = {
        mappingId: mapping?.clientId || null,
        storeKey: group.storeKey,
        name: this.sourceLiteral(mapping?.sourceFieldKey || this.form.sourceFieldKey),
        dataType: group.valueTypes.find((value) => (
          this.normalizeMappingValueType(value)
            === this.normalizeMappingValueType(
              this.sourceValueType(mapping?.sourceFieldKey || this.form.sourceFieldKey),
            )
        )) || group.valueTypes.find((value) => this.mappingValueTypesCompatible(
          this.sourceValueType(mapping?.sourceFieldKey || this.form.sourceFieldKey),
          value,
        )) || null,
      };
      this.customFieldOpen = true;
      this.$nextTick(() => this.$refs.customFieldName?.focus());
    },
    onCustomFieldStoreChanged() {
      this.customFieldDraft.dataType = this.customFieldTypeOptions[0]?.value || null;
    },
    closeCustomField() {
      if (this.creatingCustomField) return;
      this.customFieldOpen = false;
      this.customFieldProblem = null;
    },
    invalidateCustomFieldRequest() {
      this.customFieldRequestGeneration += 1;
      this.creatingCustomField = false;
    },
    invalidateDestinationSchemaRequest() {
      const requestId = this.destinationSchemaRequestId;
      this.destinationSchemaRequestId = null;
      if (!Number.isInteger(requestId)) return;
      this.$store.dispatch(
        'REGISTERS/relationships/INVALIDATE_DESTINATION_SCHEMA_REQUEST',
        requestId,
      );
    },
    invalidateEditorRequests() {
      this.invalidateCustomFieldRequest();
      this.invalidateDestinationSchemaRequest();
    },
    resolveDestinationSchema(payload) {
      const pending = this.$store.dispatch(
        'REGISTERS/relationships/RESOLVE_DESTINATION_SCHEMA',
        payload,
      );
      const requestId = this.schemaRequestId;
      this.destinationSchemaRequestId = requestId;
      return pending.finally(() => {
        if (this.destinationSchemaRequestId === requestId) {
          this.destinationSchemaRequestId = null;
        }
      });
    },
    customFieldContextIdentity(targetContext) {
      try {
        return JSON.stringify(targetContext ?? null);
      } catch (err) {
        return String(targetContext ?? '');
      }
    },
    beginCustomFieldRequest(registerId, targetKind, targetContext, mappingId) {
      const targetContextIdentity = this.customFieldContextIdentity(targetContext);
      let targetContextSnapshot = targetContext ?? null;
      try {
        targetContextSnapshot = JSON.parse(targetContextIdentity);
      } catch (err) {
        // Target contexts are JSON contracts. Keep the original only as a fail-closed identity
        // fallback if a malformed local test value cannot be cloned.
      }
      this.customFieldRequestGeneration += 1;
      return {
        generation: this.customFieldRequestGeneration,
        registerId: String(registerId),
        workspaceRequestId: this.workspaceRequestId,
        relationshipId: String(this.relationship?.id || ''),
        targetKind: String(targetKind || ''),
        targetContext: targetContextSnapshot,
        targetContextIdentity,
        mappingId,
      };
    },
    isCustomFieldRequestCurrent(request) {
      const registerId = this.register?.id || this.register?.Id;
      const targetContext = this.destinationSchema?.targetContext
        ?? this.relationship?.targetContext
        ?? null;
      return Boolean(
        request
        && this.value
        && request.generation === this.customFieldRequestGeneration
        && request.registerId === String(registerId || '')
        && request.workspaceRequestId === this.workspaceRequestId
        && request.relationshipId === String(this.relationship?.id || '')
        && request.targetKind === String(this.form.targetKind || '')
        && request.targetContextIdentity === this.customFieldContextIdentity(targetContext),
      );
    },
    continueCustomFieldRequest(request) {
      if (this.isCustomFieldRequestCurrent(request)) return true;
      if (request?.generation === this.customFieldRequestGeneration) {
        this.invalidateCustomFieldRequest();
      }
      return false;
    },
    async createCustomField() {
      if (!this.canCreateCustomField) return;
      const registerId = this.register?.id || this.register?.Id;
      if (!registerId) {
        this.customFieldProblem = {
          detail: 'The current register could not be identified. Close this dialog, refresh the register, and try again.',
        };
        return;
      }
      const name = this.customFieldDraft.name.trim();
      const targetContext = this.destinationSchema?.targetContext
        ?? this.relationship?.targetContext
        ?? null;
      const request = this.beginCustomFieldRequest(
        registerId,
        this.form.targetKind,
        targetContext,
        this.customFieldDraft.mappingId,
      );
      const command = {
        operation: 'create',
        targetKind: request.targetKind,
        targetContext: request.targetContext,
        storeKey: this.customFieldDraft.storeKey,
        attributeKey: name,
        dataType: this.customFieldDraft.dataType,
        displayLabel: name,
      };

      this.creatingCustomField = true;
      this.customFieldProblem = null;
      try {
        const grant = await relationshipService.createApprovalGrant({
          purpose: 'dynamicSchemaChange',
          registerId: request.registerId,
          command,
        });
        if (!this.continueCustomFieldRequest(request)) return;
        const token = grant?.approvalGrantToken;
        if (typeof token !== 'string' || !token.trim()) {
          throw { detail: 'The server did not return approval for this custom property.' };
        }
        const created = await relationshipService.createDynamicDefinition(
          request.registerId,
          { approvalGrantToken: token, command },
          createRelationshipRequestKey('create-custom-property'),
        );
        if (!this.continueCustomFieldRequest(request)) return;
        await this.resolveDestinationSchema({
          targetKind: request.targetKind,
          targetContext: request.targetContext,
        });
        if (!this.continueCustomFieldRequest(request)) return;
        await this.$nextTick();
        if (!this.continueCustomFieldRequest(request)) return;

        const destination = this.destinationOptions.find(
          (option) => option.definitionRef === created?.definitionRef,
        );
        if (!destination) {
          if (!this.continueCustomFieldRequest(request)) return;
          this.customFieldNotice = `“${name}” was created. Reopen this relationship if it does not yet appear in the property list.`;
          this.customFieldOpen = false;
          return;
        }

        if (!this.continueCustomFieldRequest(request)) return;
        let mapping = this.mappings.find(
          (item) => item.clientId === request.mappingId,
        );
        if (!mapping) mapping = this.mappings.find((item) => !item.destination) || this.addMapping();
        mapping.destination = destination;
        this.selectRequiredTransform(mapping);
        this.destinationNamespaceFilter = 'custom';
        this.destinationCategoryFilter = 'all';
        if (!this.continueCustomFieldRequest(request)) return;
        this.customFieldNotice = `“${name}” was created and selected as the target property.`;
        this.customFieldOpen = false;
      } catch (err) {
        if (!this.continueCustomFieldRequest(request)) return;
        this.customFieldProblem = toRelationshipProblem(
          err,
          'The custom property could not be created. No relationship change was applied.',
        );
      } finally {
        if (this.isCustomFieldRequestCurrent(request)) {
          this.creatingCustomField = false;
        } else if (request.generation === this.customFieldRequestGeneration) {
          this.invalidateCustomFieldRequest();
        }
      }
    },
    namespaceColor(namespace) {
      if (this.isBuiltInDestination({ namespace })) return 'blue';
      if (this.isLinkDestination({ namespace })) return 'teal';
      if (namespace === DESTINATION_NAMESPACE_LABELS.dynamicAttribute || namespace === 'Custom') return 'purple';
      if (namespace === DESTINATION_NAMESPACE_LABELS.definedCustomField) return 'purple';
      return 'grey';
    },
    buildPayload() {
      const relationship = this.relationship || {};
      const declaration = {
        name: this.form.name,
        sourceFieldKey: this.form.sourceFieldKey,
        targetKind: this.form.targetKind,
        targetContext: relationship.targetContext || null,
        matchField: this.form.matchField,
        matchMode: relationship.matchMode || RELATIONSHIP_DEFAULT_MATCH_MODE,
        onMiss: this.form.onMiss,
        applicability: this.form.applicabilityKind === 'declarativeFilter'
          ? { kind: 'declarativeFilter', filter: this.persistedApplicabilityFilter }
          : { kind: 'allRows' },
        mapping: this.compiledMappings.map((mapping) => ({
          sourceFieldKey: mapping.sourceFieldKey,
          destination: mapping.destination.destination,
          onCreate: mapping.onCreate || 'set',
          onExisting: 'ignore',
          direction: 'both',
          transform: this.normalizeTransform(mapping.transform),
          onError: mapping.onError,
        })),
        createDefaults: Array.isArray(relationship.createDefaults) ? relationship.createDefaults : [],
        syncMode: 'bidirectional',
        displayRole: this.form.displayRole,
        runOnImport: true,
      };

      if (!this.isEditMode) return declaration;

      // Only members the server accepts on PATCH may be diffed. sourceFieldKey is declaration-only,
      // so an edit can never propose a new source field even if the form value drifted.
      return RELATIONSHIP_PATCH_FIELDS.reduce((patch, field) => {
        const currentValue = field === 'mapping'
          ? (Array.isArray(relationship.mapping) ? relationship.mapping : [])
          : field === 'createDefaults'
            ? (Array.isArray(relationship.createDefaults) ? relationship.createDefaults : [])
            : field === 'applicability'
              ? (relationship.applicability || { kind: 'allRows' })
              : field === 'targetContext'
                ? (relationship.targetContext || null)
                : relationship[field];
        if (!this.valuesEqual(declaration[field], currentValue)) patch[field] = declaration[field];
        return patch;
      }, {});
    },
    valuesEqual(left, right) {
      try { return JSON.stringify(left) === JSON.stringify(right); } catch (err) { return left === right; }
    },
    advanceStep() {
      if (!this.canContinue || this.step >= this.steps.length) return;
      this.step += 1;
      this.focusStepHeading();
    },
    previousStep() {
      if (this.step <= 1) return;
      this.invalidateEditorRequests();
      this.step -= 1;
      this.focusStepHeading();
    },
    focusStepHeading() {
      this.$nextTick(() => {
        const heading = this.$el?.querySelector('section h3');
        if (!heading) return;
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      });
    },
    submit() { this.$emit('save', this.buildPayload()); },
  },
};
</script>

<style lang="scss" scoped>
/* The Vuetify theme's text_secondary is #C8C8C8 (plugins/vuetify.js), about 1.6:1 on these
   surfaces - unreadable. This is the muted grey the sibling relationship surfaces already use
   (RelationshipsTab, RegisterAdminWorkspace) at ~6:1. Fixing the theme token itself is an
   app-wide audit, not this dialog's call. */
.relationship-editor { --relationship-muted: #59635f; height: min(88vh, 880px); min-height: 580px; background: var(--v-bg2-base); }
.relationship-editor__shell { display: flex; width: 100%; height: 100%; min-width: 0; overflow: hidden; }
.relationship-editor__sidebar { display: flex; width: 248px; min-width: 248px; flex-direction: column; color: #fff; background: linear-gradient(180deg, #0a2626 0%, #061a1a 50%, #021a1a 100%); }
.relationship-editor__brand { display: flex; align-items: center; gap: var(--ds-space-sm); padding: var(--ds-space-lg); }
.relationship-editor__brand-icon { display: flex; width: 40px; height: 40px; flex: 0 0 40px; align-items: center; justify-content: center; border-radius: var(--ds-radius-md); background: rgba(255, 255, 255, 0.14); }
.relationship-editor__brand > div:last-child { display: flex; min-width: 0; flex-direction: column; }
.relationship-editor__brand strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: var(--ds-font-family-heading); font-size: var(--ds-font-subtitle); }
.relationship-editor__brand span { overflow: hidden; margin-top: 2px; opacity: 0.72; text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-caption); }
/* Spacing comes from a gap rather than the icon's `left` prop: that prop's negative margin pulled
   the icon into the chip's padding, where the label's overflow rule then clipped it. */
.relationship-editor__scope-chip { max-width: 220px; flex: 0 0 auto; }
.relationship-editor__scope-chip ::v-deep .v-chip__content { gap: 6px; }
.relationship-editor__scope-chip ::v-deep .v-icon { flex: 0 0 auto; }
.relationship-editor__scope-chip-label { overflow: hidden; min-width: 0; text-overflow: ellipsis; white-space: nowrap; }
.relationship-editor__steps { display: flex; flex: 1; flex-direction: column; gap: var(--ds-space-xs); padding: var(--ds-space-sm) 0; }
.relationship-editor__step { position: relative; display: flex; min-height: 60px; align-items: center; gap: var(--ds-space-sm); padding: var(--ds-space-sm) var(--ds-space-lg); }
.relationship-editor__step--active { background: rgba(3, 98, 76, 0.42); }
.relationship-editor__step--active::before { position: absolute; top: 0; bottom: 0; left: 0; width: 4px; border-radius: 0 2px 2px 0; background: var(--v-secondary-base); content: ''; }
.relationship-editor__step--pending { opacity: 0.58; }
.relationship-editor__step-number { display: flex; width: 32px; height: 32px; flex: 0 0 32px; align-items: center; justify-content: center; border-radius: 50%; background: rgba(255, 255, 255, 0.16); font-size: var(--ds-font-body-sm); font-weight: 600; }
.relationship-editor__step--active .relationship-editor__step-number, .relationship-editor__step--complete .relationship-editor__step-number { background: var(--v-primary-base); }
.relationship-editor__step-copy { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.relationship-editor__step-copy strong { font-size: var(--ds-font-body-sm); }
.relationship-editor__step-copy small { margin-top: 2px; opacity: 0.76; font-size: var(--ds-font-caption); }
.relationship-editor__sidebar-status { display: grid; grid-template-columns: 1fr auto; gap: var(--ds-space-sm); padding: var(--ds-space-md) var(--ds-space-lg); border-top: 1px solid rgba(255, 255, 255, 0.1); font-size: var(--ds-font-caption); }
.relationship-editor__sidebar-status .v-progress-linear { grid-column: 1 / -1; }
.relationship-editor__pane { display: flex; min-width: 0; flex: 1; flex-direction: column; background: var(--v-bg2-base); }
.relationship-editor__body { min-height: 0; flex: 1; }
.relationship-editor__body section h3:focus-visible { outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color); outline-offset: var(--ds-focus-ring-offset); }
.relationship-field-heading { display: flex; min-height: 24px; align-items: center; margin-bottom: var(--ds-space-xs); }
.relationship-field-heading .ds-field-label { min-width: 0; margin-bottom: 0; }
.relationship-source-key { overflow: hidden; color: var(--relationship-muted); text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-caption); }
.relationship-population { display: flex; align-items: flex-start; gap: var(--ds-space-xs); margin: 0; color: var(--relationship-muted); font-size: var(--ds-font-caption); line-height: 1.45; }
.relationship-population .v-icon { margin-top: 1px; }

.target-capability-summary { display: flex; align-items: center; justify-content: space-between; gap: var(--ds-space-md); margin: 0 var(--ds-space-sm) var(--ds-space-sm); padding: var(--ds-space-sm) var(--ds-space-md); border: 1px solid rgba(3, 98, 76, 0.22); border-radius: var(--ds-radius-md); background: var(--ds-surface-active); }
.target-capability-summary__identity { display: flex; align-items: center; min-width: 0; gap: var(--ds-space-sm); }
.target-capability-summary__identity > div { display: flex; min-width: 0; flex-direction: column; }
.target-capability-summary__identity strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-body-sm); }
.target-capability-summary__identity span { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.target-capability-summary__operations { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: var(--ds-space-xs); }


.mapping-row { display: flex; gap: var(--ds-space-md); padding: var(--ds-space-md); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.relationship-mapping-toolbar { display: flex; align-items: center; justify-content: space-between; gap: var(--ds-space-md); padding-bottom: var(--ds-space-md); border-bottom: 1px solid var(--ds-border-default); }
.relationship-mapping-toolbar > div:first-child { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.relationship-mapping-toolbar > div:first-child > span { color: var(--relationship-muted); font-size: var(--ds-font-body-sm); }
.relationship-mapping-actions { display: flex; align-items: center; gap: var(--ds-space-sm); }
.relationship-mapping-actions > span { color: var(--relationship-muted); white-space: nowrap; font-size: var(--ds-font-caption); }
.mapping-safety-summary { display: flex; align-items: flex-start; gap: var(--ds-space-sm); padding: var(--ds-space-md); border: 1px solid rgba(3, 98, 76, 0.24); border-radius: var(--ds-radius-md); background: var(--ds-surface-active); }
.mapping-safety-summary > div { display: flex; min-width: 0; flex-direction: column; gap: 2px; }
.mapping-safety-summary span { color: var(--relationship-muted); font-size: var(--ds-font-body-sm); }
.property-support { display: flex; flex-direction: column; gap: var(--ds-space-sm); padding: var(--ds-space-md); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.property-support__header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--ds-space-md); }
.property-support__copy { display: flex; min-width: 0; align-items: flex-start; gap: var(--ds-space-sm); flex: 1; }
.property-support__copy > div { display: flex; min-width: 0; flex-direction: column; }
.property-support__copy span, .property-support > small, .property-support__note { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.property-support__cta { flex-shrink: 0; border-radius: var(--ds-radius-sm) !important; }
.property-support__stats { display: flex; flex-wrap: wrap; align-items: center; gap: var(--ds-space-xs); padding-top: var(--ds-space-xs); border-top: 1px solid var(--ds-border-subtle); }
.property-support__note { margin-left: auto; }
.property-stat-chip { border-radius: var(--ds-radius-sm) !important; font-weight: 500; }
.relationship-mapping-count { padding: 3px 8px; border: 1px solid var(--ds-border-subtle); border-radius: var(--ds-radius-sm); background: var(--v-bg2-base); color: var(--relationship-muted); font-size: var(--ds-font-caption); white-space: nowrap; }
.relationship-mapping-btn { border-radius: var(--ds-radius-sm) !important; }
.relationship-mapping-add-bottom { min-height: 44px; border-radius: var(--ds-radius-sm) !important; border-style: dashed !important; }
.relationship-mapping-add-bottom:hover { border-style: solid !important; }
.custom-property-editor { padding: var(--ds-space-md); border: 1px solid rgba(3, 98, 76, 0.32); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); box-shadow: var(--ds-shadow-sm); }
.custom-property-editor__header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--ds-space-md); }
.custom-property-editor__header > div { display: flex; min-width: 0; flex-direction: column; }
.custom-property-editor__header span, .custom-property-editor__footer > span { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.custom-property-editor__footer { display: flex; align-items: center; justify-content: space-between; gap: var(--ds-space-md); padding-top: var(--ds-space-md); border-top: 1px solid var(--ds-border-subtle); }
.custom-property-editor__footer > div { display: flex; flex: 0 0 auto; align-items: center; gap: var(--ds-space-xs); }
.relationship-mapping-empty { display: flex; align-items: center; gap: var(--ds-space-md); margin-bottom: var(--ds-space-md); padding: var(--ds-space-lg); border: 1px dashed var(--ds-border-default); border-radius: var(--ds-radius-md); }
.relationship-mapping-empty > div { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.relationship-mapping-empty span { color: var(--relationship-muted); font-size: var(--ds-font-body-sm); }
.mapping-row__number { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; flex: 0 0 28px; border-radius: 50%; background: var(--v-primary-base); color: white; font-weight: 600; }
.mapping-row__fields { min-width: 0; flex: 1; }
.mapping-row__controls { gap: var(--ds-space-sm); }
.mapping-row__inline-custom-btn { border-radius: var(--ds-radius-sm) !important; border-style: dashed !important; font-weight: 600; }
.mapping-row__inline-custom-btn:hover { border-style: solid !important; }
.ds-dialog__btn-back { border-radius: var(--ds-radius-sm) !important; }
.mapping-row__sentence { display: flex; align-items: flex-start; gap: var(--ds-space-xs); padding: var(--ds-space-sm) var(--ds-space-md); border-radius: var(--ds-radius-sm); color: var(--relationship-muted); background: var(--v-bg2-base); font-size: var(--ds-font-body-sm); }
.mapping-row__sentence strong { color: var(--v-text-base); }
/* Neutral surface rather than the shared --warning variant's amber wash: saving nothing is a
   fact, not a warning, and severity here belongs to the icon. */
.relationship-save-note { border: 1px solid var(--ds-border-default); background: var(--v-bg3-base); }
.relationship-save-note .ds-mode-banner__icon { color: var(--relationship-muted); }
.mapping-row__advanced { border: 1px solid var(--ds-border-subtle); border-radius: var(--ds-radius-sm); }
.mapping-row__advanced .v-expansion-panel { background: transparent !important; }
.mapping-row__advanced .v-expansion-panel-header { min-height: 44px; padding: var(--ds-space-sm) var(--ds-space-md); color: var(--relationship-muted); font-size: var(--ds-font-body-sm); }
.mapping-row__fixed-policy { display: flex; flex-wrap: wrap; gap: var(--ds-space-sm) var(--ds-space-md); color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.mapping-row__fixed-policy span { display: inline-flex; align-items: center; gap: var(--ds-space-xs); }
.mapping-row__policy-field { width: 200px; max-width: 100%; flex: 0 1 200px; }
.mapping-row__policy-field--error { width: 240px; flex-basis: 240px; }

.destination-picker-controls {
  position: sticky;
  top: 0;
  z-index: 3;
  padding: var(--ds-space-sm);
  border-bottom: 1px solid var(--ds-border-subtle);
  background: var(--v-bg3-base);
}
.destination-picker-segmented {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: var(--ds-radius-md, 8px);
  background: var(--v-bg2-base);
  border: 1px solid var(--ds-border-subtle);
}
.destination-picker-segmented__item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 32px;
  padding: 4px 10px;
  border: none;
  border-radius: var(--ds-radius-sm, 4px);
  background: transparent;
  color: var(--relationship-muted);
  font-size: var(--ds-font-caption);
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
  user-select: none;
  white-space: nowrap;

  .v-icon {
    font-size: 14px !important;
    color: inherit;
    transition: color 0.15s ease;
  }
}
.destination-picker-segmented__item:hover:not(.destination-picker-segmented__item--active) {
  color: var(--v-text-base);
  background: rgba(0, 0, 0, 0.03);
}
.destination-picker-segmented__item:focus-visible {
  outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
  outline-offset: var(--ds-focus-ring-offset);
}
.destination-picker-segmented__item--active {
  background: var(--v-bg3-base) !important;
  color: var(--v-primary-base) !important;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);

  .v-icon {
    color: var(--v-primary-base) !important;
  }
}
.destination-picker-segmented__label {
  line-height: 1.2;
}
.destination-picker-segmented__count {
  padding: 1px 5px;
  border-radius: var(--ds-radius-sm, 4px);
  background: var(--v-bg1-base);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0.85;

  .destination-picker-segmented__item--active & {
    background: rgba(3, 98, 76, 0.1);
    color: var(--v-primary-base);
    opacity: 1;
  }
}
.destination-picker-controls__category { margin-top: var(--ds-space-sm); }

.relationship-review-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--ds-space-md); }
.relationship-review-card { display: flex; flex-direction: column; gap: var(--ds-space-xs); min-width: 0; padding: var(--ds-space-md); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.relationship-review-card--wide { grid-column: 1 / -1; }
.relationship-review-card__label { color: var(--relationship-muted); font-size: var(--ds-font-caption); text-transform: uppercase; letter-spacing: 0.04em; }
.relationship-review-summary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0; padding: 0; overflow: hidden; }
.relationship-review-summary__row { display: flex; flex-direction: column; gap: var(--ds-space-xs); min-width: 0; padding: var(--ds-space-md); border-bottom: 1px solid var(--ds-border-default); }
.relationship-review-summary__row:nth-child(odd):not(.relationship-review-summary__row--wide) { border-right: 1px solid var(--ds-border-default); }
.relationship-review-summary__row--wide { grid-column: 1 / -1; }
.relationship-review-summary__row:last-child { border-bottom: 0; }
.relationship-review-summary__row small { color: var(--relationship-muted); }
.relationship-review-card__rail, .relationship-review-map { display: flex; align-items: center; flex-wrap: wrap; gap: var(--ds-space-sm); }
.relationship-review-map + .relationship-review-map { margin-top: var(--ds-space-xs); }
.relationship-review-map small { width: 100%; color: var(--relationship-muted); }
.relationship-schema-unavailable .ds-empty-state__icon { color: var(--v-error-base); }
.relationship-review-map code { overflow-wrap: anywhere; color: var(--relationship-muted); background: transparent; font-size: var(--ds-font-caption); }

@media (max-width: 980px) {
  .relationship-editor__sidebar { width: 220px; min-width: 220px; }
  .relationship-editor__brand, .relationship-editor__step { padding-right: var(--ds-space-md); padding-left: var(--ds-space-md); }
  .relationship-editor__body { padding: var(--ds-space-md) !important; }
  .target-capability-summary { align-items: flex-start; flex-direction: column; }
  .target-capability-summary__operations { justify-content: flex-start; }
  .property-support__header { flex-direction: column; gap: var(--ds-space-sm); }
  .property-support__note { margin-left: 0; }
  .custom-property-editor__footer { align-items: flex-start; flex-direction: column; }
}

@media (prefers-reduced-motion: reduce) {
  .relationship-editor *, .relationship-editor *::before, .relationship-editor *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
}
</style>

<!-- Unscoped: v-menu teleports its content to the [data-app] root, so it is not a descendant of
     this component and carries no scoped data-v attribute. Reach is guaranteed by the unique
     contentClass .destination-picker-menu. -->
<style lang="scss">
.v-application .v-menu__content.destination-picker-menu {
  min-width: 380px !important;
  max-width: 440px !important;
  width: 380px !important;
  box-sizing: border-box !important;

  .v-list {
    padding-top: 0 !important;
    overflow-x: hidden !important;
  }

  .v-list-item {
    min-height: 48px !important;
    padding: var(--ds-space-xs) var(--ds-space-md) !important;
  }

  .v-list-item__content {
    min-width: 0 !important;
    overflow: hidden !important;
  }

  .v-list-item__title {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: var(--ds-space-xs) !important;
    min-width: 0 !important;
    font-size: var(--ds-font-body-sm) !important;
    font-weight: 500 !important;
  }

  .v-list-item__title > span:first-child {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    min-width: 0 !important;
    flex: 1 !important;
  }

  .v-list-item__subtitle {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    color: var(--relationship-muted) !important;
    font-size: var(--ds-font-caption) !important;
    margin-top: 2px !important;
  }
}

@media (max-width: 600px) {
  .v-application .v-menu__content.destination-picker-menu {
    min-width: calc(100vw - 32px) !important;
    max-width: calc(100vw - 32px) !important;
    width: calc(100vw - 32px) !important;
  }
}
</style>
